import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Label, Select, Textarea } from '@/components/ui'
import { FileText, Copy, Check, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

type GenerationType = 'paragraphs' | 'sentences' | 'words'

const LOREM_WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
  'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
  'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
  'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
  'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'perspiciatis', 'unde',
  'omnis', 'iste', 'natus', 'error', 'voluptatem', 'accusantium', 'doloremque',
  'laudantium', 'totam', 'rem', 'aperiam', 'eaque', 'ipsa', 'quae', 'ab', 'illo',
  'inventore', 'veritatis', 'quasi', 'architecto', 'beatae', 'vitae', 'dicta',
  'explicabo', 'nemo', 'ipsam', 'quia', 'voluptas', 'aspernatur', 'aut', 'odit',
  'fugit', 'consequuntur', 'magni', 'dolores', 'eos', 'ratione', 'sequi',
  'nesciunt', 'neque', 'porro', 'quisquam', 'nihil', 'impedit', 'quo', 'minus',
]

export function LoremIpsum() {
  const [output, setOutput] = useState('')
  const [count, setCount] = useState(3)
  const [type, setType] = useState<GenerationType>('paragraphs')
  const [startWithLorem, setStartWithLorem] = useState(true)
  const [copied, setCopied] = useState(false)

  const randomWord = () => LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)]

  const generateSentence = (isFirst = false): string => {
    const length = Math.floor(Math.random() * 10) + 5
    const words: string[] = []

    for (let i = 0; i < length; i++) {
      if (i === 0 && isFirst && startWithLorem) {
        words.push('Lorem')
      } else if (i === 1 && isFirst && startWithLorem) {
        words.push('ipsum')
      } else {
        words.push(randomWord())
      }
    }

    // Capitalize first word
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1)

    return words.join(' ') + '.'
  }

  const generateParagraph = (isFirst = false): string => {
    const sentenceCount = Math.floor(Math.random() * 4) + 4
    const sentences: string[] = []

    for (let i = 0; i < sentenceCount; i++) {
      sentences.push(generateSentence(isFirst && i === 0))
    }

    return sentences.join(' ')
  }

  const generate = () => {
    let result = ''

    switch (type) {
      case 'paragraphs':
        const paragraphs: string[] = []
        for (let i = 0; i < count; i++) {
          paragraphs.push(generateParagraph(i === 0))
        }
        result = paragraphs.join('\n\n')
        break

      case 'sentences':
        const sentences: string[] = []
        for (let i = 0; i < count; i++) {
          sentences.push(generateSentence(i === 0))
        }
        result = sentences.join(' ')
        break

      case 'words':
        const words: string[] = []
        for (let i = 0; i < count; i++) {
          if (i === 0 && startWithLorem) {
            words.push('Lorem')
          } else if (i === 1 && startWithLorem) {
            words.push('ipsum')
          } else {
            words.push(randomWord())
          }
        }
        result = words.join(' ')
        break
    }

    setOutput(result)
  }

  const copyOutput = async () => {
    if (!output) return

    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const getStats = () => {
    if (!output) return { words: 0, chars: 0, paragraphs: 0 }
    return {
      words: output.split(/\s+/).filter(Boolean).length,
      chars: output.length,
      paragraphs: output.split('\n\n').filter(Boolean).length,
    }
  }

  const stats = getStats()

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Lorem Ipsum Generator</h1>
            <p className="text-muted-foreground">Generate placeholder text for your designs</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[350px_1fr]">
        {/* Options */}
        <Card className="glass h-fit">
          <CardHeader>
            <CardTitle>Options</CardTitle>
            <CardDescription>Configure text generation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Generate</Label>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as GenerationType)}
              >
                <option value="paragraphs">Paragraphs</option>
                <option value="sentences">Sentences</option>
                <option value="words">Words</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Count: {count}</Label>
              <input
                type="range"
                min="1"
                max={type === 'words' ? 100 : type === 'sentences' ? 20 : 10}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={startWithLorem}
                onChange={(e) => setStartWithLorem(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm">Start with "Lorem ipsum"</span>
            </label>

            <Button onClick={generate} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>Generated lorem ipsum text</CardDescription>
              </div>
              {output && (
                <Button variant="outline" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {output ? (
              <>
                <Textarea
                  value={output}
                  readOnly
                  className="min-h-[300px] font-serif"
                />
                <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
                  <span>{stats.paragraphs} paragraphs</span>
                  <span>{stats.words} words</span>
                  <span>{stats.chars} characters</span>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Click Generate to create lorem ipsum text</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
